import React from "react";
import { Technology, Integration } from "../../../../../../redux/slices/projects/Types";
import IntegrationTimelineSection, {
  IntegrationPhase,
  getNextIntegrationPhase,
  isIntegrationPhaseComplete,
  resolveDefaultIntegrationPhase,
} from "./IntegrationTimelineSection";
import IntegrationSandboxSection from "./IntegrationSandboxSection";
import "./ProjectDetailsIntegrationTab.scss";

type ProjectDetailsIntegrationTabProps = {
  selectedTechnology: Technology;
  integrations: Integration[];
  selectedIntegrationId: number;
};

const ProjectDetailsIntegrationTab: React.FC<ProjectDetailsIntegrationTabProps> = ({
  selectedTechnology,
  integrations,
  selectedIntegrationId,
}) => {
  const selectedIntegration =
    integrations.find(
      (integration) => integration.integration_id === selectedIntegrationId
    ) || integrations[0];

  const [selectedPhase, setSelectedPhase] = React.useState<IntegrationPhase>(
    () =>
      selectedIntegration
        ? resolveDefaultIntegrationPhase(selectedIntegration)
        : "Onboarding"
  );

  const previousTimelineRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (selectedIntegration) {
      setSelectedPhase(resolveDefaultIntegrationPhase(selectedIntegration));
      previousTimelineRef.current = JSON.stringify(
        selectedIntegration.timeline ?? {}
      );
    }
  }, [selectedIntegration?.integration_id]);

  React.useEffect(() => {
    if (!selectedIntegration) return;

    const timelineKey = JSON.stringify(selectedIntegration.timeline ?? {});

    if (previousTimelineRef.current === null) {
      previousTimelineRef.current = timelineKey;
      return;
    }

    if (previousTimelineRef.current === timelineKey) return;
    previousTimelineRef.current = timelineKey;

    setSelectedPhase((currentPhase) => {
      if (!isIntegrationPhaseComplete(selectedIntegration, currentPhase)) {
        return currentPhase;
      }

      return getNextIntegrationPhase(currentPhase) ?? currentPhase;
    });
  }, [selectedIntegration]);

  if (!selectedIntegration) {
    return (
      <div className="project-details-card project-details-card--empty">
        <p>No integrations available for this technology</p>
      </div>
    );
  }

  return (
    <div className="project-details-integration-tab">
      <IntegrationTimelineSection
        integration={selectedIntegration}
        selectedPhase={selectedPhase}
        onPhaseSelect={setSelectedPhase}
      />

      <IntegrationSandboxSection technology={selectedTechnology} />
    </div>
  );
};

export default ProjectDetailsIntegrationTab;
